/**
 * adminNotificationStore — Notifications / dead-letter slice of the admin
 * store (Zustand).
 *
 * Endpoints:
 *   GET  /api/admin/notifications/dead-letters            — list dead-letter events
 *   POST /api/admin/notifications/dead-letters/:id/replay — replay an event
 */
import { API_URL } from "../../constants";
import { authHeaders, resolveToken } from "./adminUtils";
import { toDisplayString } from "../../utils/toDisplayString";

export const notificationInitialState = {
  // Admin notifications
  notifications: [],
  notificationsLoading: false,
  notificationsError: null,
  notificationsTotal: 0,
  notificationsPages: 1,
  notificationDetail: null,
  notificationDetailLoading: false,
  notificationDetailError: null,
  unreadCount: 0,
  unreadCountLoading: false,
  unreadCountError: null,

  // Dead letters
  deadLetters: [],
  deadLettersLoading: false,
  deadLettersError: null,
  deadLettersTotal: 0,
  notificationMutationLoading: false,
  notificationMutationError: null,
};

/** Normalize the dead-letters payload into `{ items, total }`. */
function normalizeListPayload(data) {
  if (!data || typeof data !== "object") return { items: [], total: 0 };

  let items = [];
  if (Array.isArray(data)) {
    items = data;
  } else if (Array.isArray(data.data)) {
    items = data.data;
  } else {
    const payload = data.data && typeof data.data === "object" ? data.data : data;
    items = payload.items || payload.deadLetters || payload.dead_letters || data.items || [];
  }

  const pagination = data.pagination || {};
  const total = pagination.total ?? data.total ?? (Array.isArray(items) ? items.length : 0);
  return { items: Array.isArray(items) ? items : [], total };
}

async function apiRequest(get, path, { method = "GET", body } = {}) {
  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: authHeaders(resolveToken(get)),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await response.text();
  let data = {};
  if (text) {
    const contentType = response.headers.get("content-type") || "";
    const looksJson = contentType.includes("application/json") ||
      text.trim().startsWith("{") || text.trim().startsWith("[");
    if (looksJson) {
      try {
        data = JSON.parse(text);
      } catch {
        data = {};
      }
    }
  }

  if (!response.ok || (data && data.error)) {
    const message = toDisplayString(
      data?.error ?? data?.message,
      `Request failed (${response.status})`
    );
    throw new Error(message);
  }

  return data;
}

/**
 * The admin navbar is rendered by every admin page, so mounting a new page
 * used to fire GET /notifications/unread-count again. With the API's rate
 * limiter that quickly turns into 429s. Share one in-flight request and cache
 * the result (including failures) for a minute.
 */
const UNREAD_TTL_MS = 60 * 1000;
let unreadFetchedAt = 0;
let unreadInFlight = null;

export function createNotificationActions(set, get) {
  return {
    // GET /api/admin/notifications
    fetchNotifications: async (params = {}) => {
      set({ notificationsLoading: true, notificationsError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(
            ([, value]) => value !== undefined && value !== null && value !== ""
          )
        ).toString();
        const data = await apiRequest(
          get,
          `/api/admin/notifications${query ? `?${query}` : ""}`
        );
        const { items, total } = normalizeListPayload(data);
        const totalPages = data?.totalPages ?? data?.pagination?.totalPages ?? 1;
        set({
          notificationsLoading: false,
          notifications: items,
          notificationsTotal: total,
          notificationsPages: totalPages,
        });
        return { success: true, notifications: items, total, pages: totalPages };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch notifications");
        set({ notificationsLoading: false, notificationsError: msg, notifications: [] });
        return { success: false, error: msg };
      }
    },

    // GET /api/admin/notifications/:id
    fetchNotification: async (id) => {
      set({ notificationDetailLoading: true, notificationDetailError: null });
      try {
        const data = await apiRequest(get, `/api/admin/notifications/${id}`);
        const detail = data?.data ?? data ?? null;
        set({ notificationDetailLoading: false, notificationDetail: detail });
        return { success: true, notification: detail };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch notification");
        set({
          notificationDetailLoading: false,
          notificationDetailError: msg,
          notificationDetail: null,
        });
        return { success: false, error: msg };
      }
    },

    // PATCH /api/admin/notifications/:id/read
    markNotificationRead: async (id) => {
      set({ notificationMutationLoading: true, notificationMutationError: null });
      try {
        const data = await apiRequest(get, `/api/admin/notifications/${id}/read`, {
          method: "PATCH",
        });
        const updated = data?.data ?? data ?? null;
        set((state) => ({
          notificationMutationLoading: false,
          notificationMutationError: null,
          notifications: state.notifications.map((item) =>
            String(item.id) === String(id) ? { ...item, ...(updated || {}), read: true } : item
          ),
          notificationDetail:
            state.notificationDetail && String(state.notificationDetail.id) === String(id)
              ? { ...state.notificationDetail, ...(updated || {}), read: true }
              : state.notificationDetail,
        }));
        return { success: true, notification: updated };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to mark notification read");
        set({ notificationMutationLoading: false, notificationMutationError: msg });
        return { success: false, error: msg };
      }
    },

    // PATCH /api/admin/notifications/read-all
    markAllNotificationsRead: async () => {
      set({ notificationMutationLoading: true, notificationMutationError: null });
      try {
        const data = await apiRequest(get, "/api/admin/notifications/read-all", {
          method: "PATCH",
        });
        set((state) => ({
          notificationMutationLoading: false,
          notificationMutationError: null,
          notifications: state.notifications.map((item) => ({ ...item, read: true })),
          unreadCount: 0,
        }));
        return { success: true, count: data?.count ?? 0 };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to mark notifications read");
        set({ notificationMutationLoading: false, notificationMutationError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/admin/notifications/unread-count
    fetchUnreadNotificationCount: async ({ force = false } = {}) => {
      const now = Date.now();
      if (!force && unreadFetchedAt && now - unreadFetchedAt < UNREAD_TTL_MS) {
        return { success: true, cached: true, count: get().unreadCount };
      }
      if (unreadInFlight) return unreadInFlight;

      set({ unreadCountLoading: true, unreadCountError: null });
      unreadInFlight = (async () => {
        try {
          const data = await apiRequest(get, "/api/admin/notifications/unread-count");
          const count = data?.count ?? data?.data?.count ?? 0;
          unreadFetchedAt = Date.now();
          set({ unreadCountLoading: false, unreadCount: count });
          return { success: true, count };
        } catch (err) {
          // Treat a failed check as a cache miss too, otherwise every page
          // mount retries immediately and deepens a 429.
          unreadFetchedAt = Date.now();
          const msg = toDisplayString(err.message, "Failed to fetch unread count");
          set({ unreadCountLoading: false, unreadCountError: msg });
          return { success: false, error: msg };
        } finally {
          unreadInFlight = null;
        }
      })();
      return unreadInFlight;
    },

    // GET /api/admin/notifications/dead-letters
    fetchDeadLetters: async (params = {}) => {
      set({ deadLettersLoading: true, deadLettersError: null });
      try {
        const query = new URLSearchParams(params).toString();
        const data = await apiRequest(
          get,
          `/api/admin/notifications/dead-letters${query ? `?${query}` : ""}`
        );
        const { items, total } = normalizeListPayload(data);
        set({ deadLettersLoading: false, deadLetters: items, deadLettersTotal: total });
        return { success: true, items, total };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch notifications");
        set({ deadLettersLoading: false, deadLettersError: msg });
        return { success: false, error: msg };
      }
    },

    // POST /api/admin/notifications/dead-letters/:id/replay
    replayDeadLetter: async (id) => {
      set({ notificationMutationLoading: true, notificationMutationError: null });
      try {
        await apiRequest(get, `/api/admin/notifications/dead-letters/${id}/replay`, {
          method: "POST",
        });
        set({
          notificationMutationLoading: false,
          notificationMutationError: null,
          successMessage: "Notification queued for replay",
        });
        return { success: true };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to replay notification");
        set({ notificationMutationLoading: false, notificationMutationError: msg });
        return { success: false, error: msg };
      }
    },
  };
}

const notificationActions = createNotificationActions;
export default notificationActions;
